'use client'

import { useMemo } from 'react'
import {
  getVisiblePages,
  isConfirmEligibleContentQuestion,
  isErrorContentQuestion,
  validateQuestionnaireAnswers,
} from './questionnaireLogic'
import type { QuestionnaireAnswers, QuestionnaireDTOType } from './types'
import type { VisibleQuestionnairePage } from './questionnaireLogic'

export type UseVisibleQuestionnairePageNavigationOptions = {
  questionnaire: QuestionnaireDTOType
  answers: QuestionnaireAnswers
  activePageIndex: number
  /** When true, treat error/confirm-eligible pages as default navigation chrome. */
  forceDefaultActions?: boolean
}

export type VisibleQuestionnairePageNavigation = {
  visiblePages: VisibleQuestionnairePage[]
  boundedActivePageIndex: number
  activePage: VisibleQuestionnairePage | undefined
  canContinueFromPage: boolean
  isActiveErrorContent: boolean
  isActiveConfirmEligibleContent: boolean
  confirmEligibleContinueLabel: string
  showDefaultNavigation: boolean
  /** Whether every question on the active page passes `validateQuestionnaireAnswers`. */
  isActivePageValid: boolean
}

export function useVisibleQuestionnairePageNavigation({
  questionnaire,
  answers,
  activePageIndex,
  forceDefaultActions = false,
}: UseVisibleQuestionnairePageNavigationOptions): VisibleQuestionnairePageNavigation {
  const visiblePages = useMemo(
    () => getVisiblePages(questionnaire, answers),
    [answers, questionnaire],
  )

  const boundedActivePageIndex =
    visiblePages.length === 0 ? 0 : Math.min(Math.max(activePageIndex, 0), visiblePages.length - 1)

  const activePage = visiblePages[boundedActivePageIndex]
  const canContinueFromPage = activePage?.allowContinue !== false
  const isActiveErrorContent =
    !forceDefaultActions &&
    (activePage?.questions.some((question) => isErrorContentQuestion(question)) ?? false)
  const isActiveConfirmEligibleContent =
    !forceDefaultActions &&
    (activePage?.questions.some((question) => isConfirmEligibleContentQuestion(question)) ?? false)
  const confirmEligibleQuestion = activePage?.questions.find((question) =>
    isConfirmEligibleContentQuestion(question),
  )
  const confirmEligibleContinueLabel = confirmEligibleQuestion?.label?.trim()
    ? confirmEligibleQuestion.label.trim()
    : 'Next'
  const showDefaultNavigation =
    forceDefaultActions || (!isActiveErrorContent && !isActiveConfirmEligibleContent)

  const isActivePageValid = useMemo(() => {
    if (!activePage) return false
    const validation = validateQuestionnaireAnswers(questionnaire, answers)
    return activePage.questions.every((question) => !validation.errors[question.questionId])
  }, [activePage, answers, questionnaire])

  return {
    visiblePages,
    boundedActivePageIndex,
    activePage,
    canContinueFromPage,
    isActiveErrorContent,
    isActiveConfirmEligibleContent,
    confirmEligibleContinueLabel,
    showDefaultNavigation,
    isActivePageValid,
  }
}
